import React, {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { BubbleMenu, BubbleMenuProps, Editor } from "@tiptap/react";
import { TiptapEditorContext } from "@src/features/libraryItem/course/contexts/TiptapEditorContext";
import EditLinkDialog from "@src/features/libraryItem/course/EditLinkDialog";
import { Button } from "@src/lib/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import TextBoldIcon from "@src/lib/icons/18px/text-bold";
import TextItalicIcon from "@src/lib/icons/18px/text-italic";
import UnorderedListIcon from "@src/lib/icons/18px/unordered-list";
import OrderedListIcon from "@src/lib/icons/18px/ordered-list";
import Table3RowsOutlineIcon from "@src/lib/icons/18px/table-3-rows-outline";
import LinkIcon from "@src/lib/icons/18px/link";
import EmojiOutlineIcon from "@src/lib/icons/18px/emoji-outline";
import EmojiPicker from "@src/lib/ui/EmojiPicker";
import { doesPromptScreenContainTable } from "@src/features/libraryItem/course/utils/tipTapTable";
import { cn } from "@src/lib/utils/cn";
import {
  glassButtonClassName,
  FLOATING_TOOLS_Z_INDEX,
  floatingToolsContainer,
  glassSurfaceOverTextClassName,
} from "./glassSurface";

// tippy.js isn't a direct dependency; take its instance type from tiptap.
type TippyInstance = Parameters<
  NonNullable<NonNullable<BubbleMenuProps["tippyOptions"]>["onCreate"]>
>[0];

type Props = {
  editor: Editor;
  /**
   * "full" adds table and emoji, for editors with no other home for them
   * (skill steps). A course screen keeps those in its settings pane, since
   * they act on the whole screen rather than the selection.
   */
  tools?: "full" | "inline";
  /** Extra tools after the formatting ones, e.g. a screen's AI rewrites. */
  extra?: ReactNode;
  /**
   * Shown above the pill, inside the bubble so clicks on it keep the editor's
   * selection (a portalled menu would blur the editor and close the pill).
   */
  panel?: ReactNode;
  onHide?: () => void;
};

/**
 * Clicking a tool keeps focus (and the selection) in the editor, so the
 * editor's own blur still hides the pill when the author clicks away.
 */
const keepEditorFocus = (event: React.MouseEvent) => event.preventDefault();

const toolClassName = (active: boolean) =>
  cn(glassButtonClassName, "size-6", active && "bg-white/20");

/**
 * The glass pill that floats above selected text: bold, italic, lists and
 * link, plus whatever the host adds. It mounts on the app root at real
 * screen size, so it stays usable on a zoomed-out course grid.
 */
const FormattingBubbleMenu: FC<Props> = ({
  editor,
  tools = "full",
  extra,
  panel,
  onHide,
}) => {
  const tiptapEditorContext = useContext(TiptapEditorContext);
  const [addLinkOpen, setAddLinkOpen] = useState(false);
  const pillRef = useRef<HTMLDivElement>(null);
  const tippyRef = useRef<TippyInstance | null>(null);
  // The tools keep focus in the editor (keepEditorFocus), which also leaves
  // tiptap's "ignore the next blur" flag set, so a later click elsewhere
  // wouldn't hide the pill. Hide it ourselves on a press outside it and the
  // editor. Only the pill is hidden: the selection stays, so the settings
  // pane's Style buttons still format it.
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const tippy = tippyRef.current;
      const target = event.target;
      if (!tippy?.state.isVisible || !(target instanceof Element)) return;
      if (
        editor.view.dom.contains(target) ||
        // The pill itself, other editor bubbles, and popovers the tools open
        // (emoji) all render outside the editor.
        target.closest("[data-tippy-root], [data-radix-popper-content-wrapper]")
      ) {
        return;
      }
      tippy.hide();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [editor]);
  const [addLinkText, setAddLinkText] = useState("");
  const [addLinkInitialLink, setAddLinkInitialLink] = useState("");
  const [addLinkSelection, setAddLinkSelection] = useState<{
    from: number;
    to: number;
  } | null>(null);

  const promptScreenContainsTable = React.useMemo(() => {
    const editorState = editor?.state;
    if (editorState) {
      return doesPromptScreenContainTable(editorState);
    }
    return false;
  }, [editor?.state]);

  const tableDisabled =
    promptScreenContainsTable && !tiptapEditorContext?.formatting.isTable;

  const onEmojiSelect = useCallback(
    (emoji: string) => {
      if (editor) {
        const insertPoint = editor.view?.state?.selection?.anchor || 1;
        editor
          .chain()
          .focus()
          .insertContentAt(insertPoint, emoji, {
            parseOptions: {
              preserveWhitespace: false,
            },
          })
          .run();
      }
    },
    [editor],
  );

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{
        duration: 10,
        interactive: true,
        placement: "top",
        // A scaled course tile would shrink the pill with it; the app root
        // keeps it at real size, above the tiles (inside a dialog, the
        // dialog: a modal makes everything outside it inert).
        appendTo: floatingToolsContainer,
        zIndex: FLOATING_TOOLS_Z_INDEX,
        onCreate: (instance) => {
          tippyRef.current = instance;
        },
        onHidden: () => onHide?.(),
      }}
      className="relative"
      shouldShow={({ editor, state }) => {
        // Only show when there's a text selection (not just cursor position)
        const { from, to } = state.selection;
        const hasSelection = from !== to;

        // Don't show if selection contains only a table
        const isTableSelected = editor.isActive("table");

        return editor.isEditable && hasSelection && !isTableSelected;
      }}
    >
      {/* Absolutely placed so opening it doesn't resize the bubble: tippy
          only re-measures on editor updates, so a taller bubble would
          slide down over the selection. */}
      {panel && !addLinkOpen && (
        <div
          className="absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2"
          onMouseDown={keepEditorFocus}
        >
          {panel}
        </div>
      )}
      <div
        ref={pillRef}
        data-testid="formatting-bubble-menu"
        // Hidden while its link dialog is up, so it never floats over it.
        className={cn(
          "flex h-8 items-center gap-0.5 rounded-full p-1",
          glassSurfaceOverTextClassName,
          addLinkOpen && "invisible",
        )}
        onMouseDown={keepEditorFocus}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Bold"
                className={toolClassName(editor.isActive("bold"))}
                onClick={() => {
                  editor.chain().focus().toggleBold().run();
                }}
              >
                <TextBoldIcon className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Bold</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Italic"
                className={toolClassName(editor.isActive("italic"))}
                onClick={() => {
                  editor.chain().focus().toggleItalic().run();
                }}
              >
                <TextItalicIcon className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Italic</TooltipContent>
          </Tooltip>

          <div aria-hidden className="mx-0.5 h-3.5 w-px bg-white/20" />

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Bullet list"
                className={toolClassName(editor.isActive("bulletList"))}
                onClick={() => {
                  editor.chain().focus().toggleBulletList().run();
                }}
              >
                <UnorderedListIcon className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Bullet List</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Numbered list"
                className={toolClassName(editor.isActive("orderedList"))}
                onClick={() => {
                  editor.chain().focus().toggleOrderedList().run();
                }}
              >
                <OrderedListIcon className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Numbered List</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Link"
                className={toolClassName(editor.isActive("link"))}
                onClick={() => {
                  const selection = editor.view.state.selection;
                  if (!selection) return;
                  const link = editor.getAttributes("link").href;
                  const text = editor.view.state.doc.textBetween(
                    selection.from,
                    selection.to,
                    " ",
                  );
                  setAddLinkText(text ?? "");
                  setAddLinkInitialLink(link ?? "");
                  setAddLinkSelection({
                    from: selection.from,
                    to: selection.to,
                  });
                  setAddLinkOpen(true);
                }}
              >
                <LinkIcon className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Link</TooltipContent>
          </Tooltip>

          {tools === "full" && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Table"
                    className={toolClassName(editor.isActive("table"))}
                    disabled={tableDisabled}
                    onClick={() => {
                      if (tiptapEditorContext?.formatting.isTable) {
                        return;
                      }
                      const startPos = editor.state.selection.$anchor.pos || 0;
                      editor
                        .chain()
                        .focus()
                        .insertContent(
                          "<p></p><table><tr><td><p></p></td><td><p></p></td></tr><tr><td><p></p></td><td><p></p></td></tr></table><p></p>",
                        )
                        .setTextSelection(startPos + 4)
                        .run();
                    }}
                  >
                    <Table3RowsOutlineIcon className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {tableDisabled ? "One table per step" : "Table"}
                </TooltipContent>
              </Tooltip>

              <Popover>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Emoji"
                        className={toolClassName(false)}
                      >
                        <EmojiOutlineIcon className="size-3.5" />
                      </Button>
                    </PopoverTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="top">Emoji</TooltipContent>
                </Tooltip>
                <PopoverContent
                  side="top"
                  align="center"
                  className="w-auto p-0"
                  sideOffset={10}
                >
                  <EmojiPicker onEmojiSelect={onEmojiSelect} />
                </PopoverContent>
              </Popover>
            </>
          )}
          {extra && (
            <>
              <div aria-hidden className="mx-0.5 h-3.5 w-px bg-white/20" />
              {extra}
            </>
          )}
        </TooltipProvider>
      </div>
      {addLinkOpen && (
        <EditLinkDialog
          open={addLinkOpen}
          onOpenChange={setAddLinkOpen}
          text={addLinkText}
          initialLink={addLinkInitialLink}
          onComplete={(text, link) => {
            if (!addLinkSelection) return;
            editor
              .chain()
              .focus()
              .setLink({ href: link, target: "_blank" })
              .insertContentAt(
                {
                  from: addLinkSelection.from,
                  to: addLinkSelection.to,
                },
                text,
              )
              .unsetMark("link")
              .focus()
              .run();
          }}
        />
      )}
    </BubbleMenu>
  );
};

export default FormattingBubbleMenu;
